import React, { useEffect, useState } from 'react';
import {
  Button,
  CircularProgress,
  Grid,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import axios from 'axios';
import moment from 'moment';
import 'moment/locale/pt-br';
import { useParams, useHistory } from 'react-router-dom';
import {
  BASE_API,
  BASE_S3,
  COMPANY_LOGO,
  COMPANY_SIGNED_IN_BACKGROUND_COLOR,
} from 'config/consts';

moment.locale('pt-br');

const PLAY_STORE_URL = 'https://play.google.com/store/search?q=sindilegis+mais&c=apps&hl=pt_BR';
const APP_STORE_URL = 'https://apps.apple.com/br/app/sindilegis-mais/id1480766309';

const useStyles = makeStyles(() => ({
  root: {
    minHeight: '100vh',
    backgroundColor: COMPANY_SIGNED_IN_BACKGROUND_COLOR || '#f5f5f5',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    padding: '2rem 1rem',
  },
  card: {
    backgroundColor: '#fff',
    borderRadius: 16,
    padding: '2rem 1.5rem',
    maxWidth: 520,
    width: '100%',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
  },
  logoWrap: {
    textAlign: 'center',
    marginBottom: '1.5rem',
  },
  logo: {
    maxWidth: 180,
    height: 'auto',
  },
  image: {
    width: '100%',
    maxHeight: 200,
    objectFit: 'cover',
    borderRadius: 10,
    marginBottom: '1.25rem',
  },
  heading: {
    textAlign: 'center',
    fontWeight: 700,
    marginBottom: '0.5rem',
  },
  bodyText: {
    lineHeight: 1.7,
    color: '#444',
    fontSize: '0.93rem',
    marginBottom: '0.75rem',
  },
  highlight: {
    fontWeight: 600,
  },
  scheduleBox: {
    backgroundColor: '#f8f9fa',
    borderRadius: 10,
    padding: '1rem 1.25rem',
    margin: '1rem 0',
    border: '1px solid #e8e8e8',
  },
  scheduleTitle: {
    fontWeight: 600,
    marginBottom: 8,
    fontSize: '0.9rem',
    color: '#333',
  },
  scheduleItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '6px 0',
    borderBottom: '1px solid #eee',
    '&:last-child': { borderBottom: 'none' },
  },
  scheduleLabel: {
    fontSize: '0.85rem',
    color: '#555',
  },
  scheduleBadge: {
    fontWeight: 700,
    fontSize: '0.85rem',
    color: '#1188E6',
    backgroundColor: '#e8f4fd',
    borderRadius: 12,
    padding: '2px 10px',
  },
  ctaSection: {
    textAlign: 'center',
    margin: '1.5rem 0 1rem',
  },
  button: {
    textTransform: 'none',
    padding: '12px 24px',
    borderRadius: 8,
    fontWeight: 600,
    fontSize: '0.95rem',
  },
  appSection: {
    textAlign: 'center',
    marginTop: '1.25rem',
    padding: '1rem',
    backgroundColor: '#fafafa',
    borderRadius: 10,
  },
  appLinks: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 12,
    marginTop: 10,
  },
  appBadge: {
    height: 44,
    width: 'auto',
  },
  regulationBox: {
    marginTop: '1.25rem',
    padding: '1rem',
    backgroundColor: '#f9f9f9',
    borderRadius: 8,
    border: '1px solid #eee',
  },
  regulationTitle: {
    fontWeight: 600,
    fontSize: '0.85rem',
    color: '#333',
    marginBottom: 6,
  },
  regulationText: {
    fontSize: '0.78rem',
    color: '#666',
    lineHeight: 1.6,
    whiteSpace: 'pre-wrap',
  },
  footer: {
    textAlign: 'center',
    marginTop: '1rem',
    fontSize: '0.8rem',
    color: '#999',
  },
}));

const formatScheduleLabel = (monthStr) => {
  const m = moment(monthStr, 'YYYY-MM');
  const lastDay = m.endOf('month').format('DD');
  const monthName = m.format('MMMM');
  return `${lastDay} de ${monthName}`;
};

function ActivityReferralLanding() {
  const classes = useStyles();
  const { referral_link } = useParams();
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [referral, setReferral] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchReferral() {
      try {
        const response = await axios.get(
          `${BASE_API}/referrals/link/${referral_link}`
        );
        setReferral(response.data);
      } catch (e) {
        setError('Link de indicação não encontrado ou inválido.');
      }
      setLoading(false);
    }
    fetchReferral();
  }, [referral_link]);

  if (loading) {
    return (
      <div className={classes.root}>
        <CircularProgress />
      </div>
    );
  }

  if (error || !referral) {
    return (
      <div className={classes.root}>
        <div className={classes.card}>
          <div className={classes.logoWrap}>
            <img src={COMPANY_LOGO} alt="Logo" className={classes.logo} />
          </div>
          <Typography variant="h5" className={classes.heading}>
            Link inválido
          </Typography>
          <Typography variant="body1" color="textSecondary" style={{ textAlign: 'center' }}>
            {error || 'Este link de indicação não é válido.'}
          </Typography>
          <div className={classes.ctaSection}>
            <Button
              className={classes.button}
              variant="contained"
              color="primary"
              onClick={() => history.push('/login')}>
              Ir para o login
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const action = referral.action || {};
  const inviterName = referral.inviter?.name || 'Um filiado';
  const logoSrc = referral.stationLogo || COMPANY_LOGO;
  const actionName = action.name || 'Indique e Concorra';
  const scheduleItems = referral.scheduleItems || [];
  const regulation = referral.regulation || null;

  return (
    <div className={classes.root}>
      <div className={classes.card}>
        <div className={classes.logoWrap}>
          {logoSrc && (
            <img src={logoSrc} alt="Logo" className={classes.logo} />
          )}
        </div>

        {action.image && (
          <img
            src={`${BASE_S3}/${action.image}`}
            alt={actionName}
            className={classes.image}
          />
        )}

        <Typography variant="h5" className={classes.heading}>
          Você foi convidado!
        </Typography>

        <Typography className={classes.bodyText}>
          <strong>{inviterName}</strong> convidou você para fazer parte do Sindilegis
          e isso pode render muito mais do que você imagina.
        </Typography>

        <Typography className={classes.bodyText}>
          Ao aceitar se filiar, vocês passam a participar do sorteio{' '}
          <strong>{actionName}</strong>, concorrendo juntos a prêmios incríveis.
        </Typography>

        <Typography className={classes.bodyText}>
          E tem mais:{' '}
          <span className={classes.highlight}>
            quanto antes você se filiar, maiores são as suas chances.
          </span>
        </Typography>

        {scheduleItems.length > 0 && (
          <div className={classes.scheduleBox}>
            <div className={classes.scheduleTitle}>
              Se você aceitar até:
            </div>
            {scheduleItems.map((item, idx) => (
              <div key={idx} className={classes.scheduleItem}>
                <span className={classes.scheduleLabel}>
                  {formatScheduleLabel(item.month)}
                </span>
                <span className={classes.scheduleBadge}>
                  {item.quantity} número{item.quantity > 1 ? 's' : ''}
                </span>
              </div>
            ))}
          </div>
        )}

        <Typography className={classes.bodyText} style={{ fontWeight: 600 }}>
          Não deixe para depois. Garanta agora sua participação!
        </Typography>

        <div className={classes.ctaSection}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Button
                className={classes.button}
                variant="contained"
                color="primary"
                fullWidth
                href="https://e.sindilegis.org.br/filiacao"
                target="_blank"
                rel="noopener noreferrer">
                Filie-se ao Sindilegis
              </Button>
            </Grid>
          </Grid>
        </div>

        <div className={classes.appSection}>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 4 }}>
            Baixe o app <strong>Sindilegis Mais</strong>:
          </Typography>
          <div className={classes.appLinks}>
            <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer">
              <img
                src="https://upload.wikimedia.org/wikipedia/commons/thumb/7/78/Google_Play_Store_badge_EN.svg/200px-Google_Play_Store_badge_EN.svg.png"
                alt="Google Play"
                className={classes.appBadge}
              />
            </a>
            <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">
              <img
                src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
                alt="App Store"
                className={classes.appBadge}
              />
            </a>
          </div>
        </div>

        <Typography variant="body2" color="textSecondary" style={{ textAlign: 'center', marginTop: '1rem' }}>
          Depois, em até 24 horas, os números da sorte estarão disponíveis no app.
        </Typography>

        {regulation && (
          <div className={classes.regulationBox}>
            <div className={classes.regulationTitle}>Regulamento</div>
            <div className={classes.regulationText}>{regulation}</div>
          </div>
        )}

        <div className={classes.footer}>
          Nos vemos na viagem! 😉
        </div>
      </div>
    </div>
  );
}

export default ActivityReferralLanding;
